export const TRANSCRIPT_SCROLL_CONTAINER_ID = "ai-triage-messages";
export const TRANSCRIPT_FOLLOW_NEAR_BOTTOM_PX = 64;
const NESTED_ANIMATION_FRAMES = 2;

/**
 * @typedef {{
 *   scrollTop: number,
 *   readonly scrollHeight: number,
 *   readonly clientHeight: number,
 *   addEventListener: (
 *     type: string,
 *     listener: EventListener,
 *     options?: boolean | AddEventListenerOptions
 *   ) => void,
 *   removeEventListener: (
 *     type: string,
 *     listener: EventListener,
 *     options?: boolean | EventListenerOptions
 *   ) => void,
 * }} TranscriptScroller
 */

/**
 * @param {Document} [root]
 * @returns {HTMLElement | null}
 */
export function transcriptScrollContainer(root = document) {
  return root.getElementById(TRANSCRIPT_SCROLL_CONTAINER_ID);
}

/**
 * @param {TranscriptScroller | null} container
 * @returns {number}
 */
export function transcriptDistanceFromBottom(container) {
  if (!container) {
    return Number.POSITIVE_INFINITY;
  }

  return container.scrollHeight - container.scrollTop - container.clientHeight;
}

/**
 * @param {TranscriptScroller | null} container
 * @param {number} [nearBottomPx]
 * @returns {boolean}
 */
export function isTranscriptNearBottom(
  container,
  nearBottomPx = TRANSCRIPT_FOLLOW_NEAR_BOTTOM_PX
) {
  return transcriptDistanceFromBottom(container) <= nearBottomPx;
}

/**
 * @param {TranscriptScroller | null} container
 */
export function followTranscriptLiveReply(container) {
  if (!container) {
    return;
  }

  container.scrollTop = container.scrollHeight;
}

/**
 * @param {{
 *   getContainer?: () => TranscriptScroller | null,
 *   scheduleFrame?: (callback: FrameRequestCallback) => number,
 * }} [options]
 */
export function createTranscriptFollowScroll({
  getContainer = transcriptScrollContainer,
  scheduleFrame = (callback) => window.requestAnimationFrame(callback),
} = {}) {
  let following = true;
  let applyingFollow = false;
  let frameId = null;
  let boundContainer = null;

  function pin() {
    following = true;
  }

  function unpin() {
    following = false;
  }

  function isFollowing() {
    return following;
  }

  function syncFromUserScroll() {
    if (applyingFollow) {
      return;
    }

    const container = getContainer();
    if (!container) {
      return;
    }

    if (isTranscriptNearBottom(container)) {
      pin();
      return;
    }

    unpin();
  }

  function applyFollow() {
    const container = getContainer();
    if (!container || !following) {
      return;
    }

    applyingFollow = true;
    followTranscriptLiveReply(container);
    applyingFollow = false;
  }

  function followLiveIfPinned() {
    if (!following || frameId != null) {
      return;
    }

    let remainingFrames = NESTED_ANIMATION_FRAMES;
    const tick = () => {
      remainingFrames -= 1;
      if (remainingFrames > 0) {
        frameId = scheduleFrame(tick);
        return;
      }

      frameId = null;
      applyFollow();
    };

    frameId = scheduleFrame(tick);
  }

  function onScroll() {
    syncFromUserScroll();
  }

  function unbind() {
    if (!boundContainer) {
      return;
    }

    boundContainer.removeEventListener("scroll", onScroll);
    boundContainer = null;
  }

  function bind() {
    const container = getContainer();
    if (!container) {
      unbind();
      return unbind;
    }

    if (boundContainer === container) {
      return unbind;
    }

    unbind();
    boundContainer = container;
    boundContainer.addEventListener("scroll", onScroll, { passive: true });
    return unbind;
  }

  return {
    pin,
    unpin,
    isFollowing,
    followLiveIfPinned,
    syncFromUserScroll,
    bind,
    unbind,
  };
}
